/*
 * @Author: zhangxiaoda zhangxiaoda@nextmar.com
 * @Date: 2025-03-11 15:54:38
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2025-12-01 15:04:57
 * @FilePath: /src/app/coupons/page.client.tsx
 * @Description: Coupons页面客户端逻辑
 */
'use client';
import { getCoupon, getCouponsList } from '@/api/coupon';
import { fetchJSONValue } from '@/api/public';
import Button from '@/components/Button';
import Coupon from '@/components/Coupon/coupon';
import { couponDateTransform } from '@/components/Coupon/handler';
import { ICouponProps } from '@/components/Coupon/type';
import Image from '@/components/Image';
import { useJump } from '@/components/MagicJump';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { SubscribeBar, useComplianceSubscriptionDisplay } from '@/components/Subscribe';
import { toast } from '@/components/Toast';
import { useReporting } from '@/hooks/useReporting';
import { useWhenTokenReady } from '@/hooks/useWhenReady';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { ECouponButtonType, ECouponType, EIsFirstOrder } from '@/utils/enums/coupon';
import { Skeleton } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

export default function CouponsPageClient() {
  const [coupons, setCoupons] = useState<ICouponProps[]>([]);
  const [firstOrderCoupons, setFirstOrderCoupons] = useState<ICouponProps[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const token = useStore((state) => state.token);
  const [couponId, setCouponId] = useState<any>(null);
  const [bannerJsonValue, setBannerJsonValue] = useState<any>(null);
  const { setShowLoginRegisterDialog } = useStore((state) => state);
  const router = useRouter();
  const { trackEvent } = useReporting();
  const toJumpTarget = useJump();
  const couponsListPromiseRef = useRef<ReturnType<typeof getCouponsList>>(null);
  const { isShowSubscribeEmail } = useComplianceSubscriptionDisplay();

  const t = useTranslations('coupons');
  const currency = useStore((state) => state.currency);

  useWhenTokenReady(() => {
    setIsMounted(true);
    getCoupons();
    if (couponId) {
      // TODO:这里为什么取了数据接口不用？
      getCoupon(couponId);
    }
  }, [token]);

  useEffect(() => {
    getCoupons(true);
  }, [currency]);

  useEffect(() => {
    getJSONValue();
  }, []);

  const getJSONValue = async () => {
    const res = await fetchJSONValue(['coupon-center-banner']);
    const _bannerJsonValue = JSON.parse(res?.data['coupon-center-banner']?.json_value) || '';
    setBannerJsonValue(_bannerJsonValue);
  };

  // 可能会被多次调用，缓存一下
  const getCouponsListRes = async (canUseCache?: boolean) => {
    if (canUseCache) {
      if (couponsListPromiseRef.current) {
        return couponsListPromiseRef.current;
      }
    }
    couponsListPromiseRef.current = getCouponsList();
    return couponsListPromiseRef.current;
  };

  const getCoupons = async (canUseCache?: boolean) => {
    try {
      const res = await getCouponsListRes(canUseCache);
      if (!res) return;

      const _firstOrderCoupons = res?.data.filter(
        (coupon: any) =>
          coupon.is_first_order === EIsFirstOrder.FIRST_ORDER && coupon.code_status === 1,
      );
      const _coupons = res?.data.filter(
        (coupon: any) =>
          coupon.is_first_order === EIsFirstOrder.NOT_FIRST_ORDER && coupon.code_status === 1,
      );

      const transFirstOrderCoupons = _firstOrderCoupons.map((coupon: any) => {
        return couponDateTransform(coupon, false, t, currency);
      });

      const transNoFirstOrderCoupons = _coupons.map((coupon: any) => {
        return couponDateTransform(coupon, false, t, currency);
      });

      setFirstOrderCoupons(transFirstOrderCoupons);
      setCoupons(transNoFirstOrderCoupons);
      setLoading(false);
    } catch (error) {
      logger.error('Failed to fetch coupons:', error);
      setLoading(false);
    }
  };

  const handleCouponAction = async (coupon_id: number, buttonText: ECouponButtonType) => {
    if (!token) {
      setCouponId(coupon_id);
      setShowLoginRegisterDialog({
        show: true,
        eventType: 'coupon_register',
      });
      return;
    }

    if (buttonText === ECouponButtonType.GET) {
      await getCoupon(coupon_id);
      toast.success(t('success'));
      setCouponId(null);
      getCoupons();
    } else if (buttonText === ECouponButtonType.SHOP) {
      router.push('/');
    }

    // 这里可以添加导航到商店页面的逻辑
    // router.push('/shop');
  };

  function getCouponType(coupon: any) {
    const type = Number(coupon.model_type);

    // 1 和2 都算作是1
    const typeMap = {
      1: ECouponType.DISCOUNT,
      2: ECouponType.DISCOUNT,
      3: ECouponType.EXCHANGE,
      4: ECouponType.SHIPPING,
    };
    return typeMap[type] || ECouponType.DISCOUNT;
  }

  const SDAmazingCouponsPageClick = (discount_code: any) => {
    try {
      const sdEventName = 'AmazingCouponsPage_Click';
      const sdEventParams = {
        coupon_id: discount_code.id,
        coupon_name: discount_code.couponName,
        btn_name: discount_code.buttonText === ECouponButtonType.GET ? 'GET' : 'SHOP',
      };

      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.log(e);
    }
  };

  const handleGoShopping = () => {
    toJumpTarget(bannerJsonValue.jumpTarget);

    // 这里可以添加导航到商店页面的逻辑
    // router.push('/shop');
  };

  const renderSkeleton = () => {
    return [1, 2, 3, 4, 5, 6].map((item) => (
      <Skeleton
        key={item}
        className="rounded-6"
        variant="rectangular"
        width={380}
        height={120}
        sx={{
          '@media (max-width: 768px)': {
            width: '100% !important',
          },
        }}
      />
    ));
  };

  // During SSR and first render, return null to avoid hydration mismatch
  if (!isMounted) {
    return null;
  }

  return (
    <div className="bg-white md:bg-[#f7f8fa] mx-14 md:mx-0">
      <div className={`container mx-auto py-26 px-0 md:px-4 max-w-[1440px]`}>
        <WebOnly>
          <h1 className="font-bb mb-4 text-center text-32">{t('title')}</h1>
          <p className="text-center mt-24 max-w-3xl mx-auto font-br mb-40">{t('subTitle')}</p>
          {isShowSubscribeEmail && (
            <div className="mb-24">
              <SubscribeBar background="#fff" className="!h-[72px] !rounded-[12px]" />
            </div>
          )}
        </WebOnly>

        <WapOnly>
          <Image
            src="/coupon/get-coupon-top{locale}.png"
            alt="banner"
            width={375}
            height={120}
            className="w-full h-auto"
          />
          {isShowSubscribeEmail && <SubscribeBar className="mt-16 mb-16" />}
        </WapOnly>

        {loading ? (
          <div className="bg-white pt-20 md:p-40 md:pt-32 rounded-[12px]">
            <Skeleton className="rounded-6" variant="rectangular" height={64} />

            <Skeleton className="mt-40 rounded-6" variant="rectangular" width={250} height={40} />

            <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16 mb-20">
              {renderSkeleton()}
            </div>
          </div>
        ) : (
          <>
            <div className="bg-white md:p-40 md:pt-32 rounded-[12px]">
              {/* 促销横幅 */}
              <WebOnly>
                <div
                  className=" h-64 mb-40 rounded-lg flex justify-between items-center pl-16 pr-16"
                  style={{
                    backgroundImage: `url(${bannerJsonValue?.bgImg})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                  }}
                >
                  <div className="flex items-center " style={{ color: bannerJsonValue?.textColor }}>
                    <span className="font-bb text-16">{bannerJsonValue?.title}</span>
                    <span className="font-bb text-20 ml-48">{bannerJsonValue?.subTitle}</span>
                  </div>
                  <Button
                    variant="contained"
                    className="h-32"
                    color="black"
                    onClick={handleGoShopping}
                    sx={{
                      borderRadius: '16px',
                    }}
                  >
                    {bannerJsonValue?.btnText}
                  </Button>
                </div>
              </WebOnly>

              {/* 首单优惠 */}
              {firstOrderCoupons.length > 0 && (
                <section className="mb-10">
                  <h2 className="text-20 font-bb mb-20">{t('firstOrderTitle')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16 mb-20">
                    {firstOrderCoupons.map((coupon) => (
                      <Coupon
                        key={coupon.id}
                        {...coupon}
                        onClick={() => {
                          SDAmazingCouponsPageClick(coupon);
                          handleCouponAction(coupon.id, coupon.buttonText);
                        }}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* 更多优惠 */}
              <section>
                <h2 className="text-20 font-bb mt-40 mb-20">{t('buyMoreTitle')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
                  {coupons.map((coupon) => (
                    <Coupon
                      key={coupon.id}
                      {...coupon}
                      couponType={getCouponType(coupon)}
                      onClick={() => {
                        SDAmazingCouponsPageClick(coupon);
                        handleCouponAction(coupon.id, coupon.buttonText);
                      }}
                    />
                  ))}
                </div>
              </section>
            </div>

            <WebOnly>
              {/* 使用条款 */}
              <section className="mt-16 mb-60 text-12 pl-40 text-auxiliary-text">
                <p className="font-medium mb-2">{t('bottomTitle')}</p>
                <ol className="pl-5 space-y-1 list-none">
                  <li>{t('bottomContent1')}</li>
                  <li>{t('bottomContent2')}</li>
                  <li>{t('bottomContent3')}</li>
                  <li>{t('bottomContent4')}</li>
                </ol>
              </section>
            </WebOnly>
          </>
        )}
      </div>
    </div>
  );
}
